/**
 * Terraformer Tank (Sapper + Mason merged) — Reshape or rebuild.
 * Switch modes to destroy or create terrain.
 */

import { registerWeapon } from '../weapons';
import { clearCircle, applyScorch, fillVerticalWall } from '../terrain/grid';
import { registerTank, setTerraformerMasonShots } from './registry';
import { DEFAULT_CRATER, AXE_SPLIT_CRATER, HUGE_CRATER } from '../terrain/crater-shapes';

// ─── Sapper Weapons ──────────────────────────────────────────────────────────

// Shot 1: Digger — deep vertical shaft, minimal combat value
const SAPPER_SHOT1 = registerWeapon({
  id: 'sapper_shot1',
  name: 'Digger',
  description: 'Punches a deep vertical shaft. Terrain tool, not a weapon.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 7,
  projectileSpeed: 0.55,
  color: 0x88ff44,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 8,            // S1 nerf: was 12
  blastRadius: 134,
  knockbackMult: 0.03,
  knockbackRadius: 1.2,
  shakeIntensity: 2.5,
  particleScale: 1.0,
  craterShape: AXE_SPLIT_CRATER,
  // Bright green energy crackle — cutting sparks
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 3 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 10,
        proj.y + (Math.random() - 0.5) * 10,
        0x44ff88,
        0.5, 1.5, 0.03, 0.06, 0.08, 0.18, 0, 0.9, true, true, 0.6,
      ] });
    }
    return null;
  },
});

// Shot 2: Trencher — terrain-carving projectile that plows through ground
const SAPPER_SHOT2 = registerWeapon({
  id: 'sapper_shot2',
  name: 'Trencher',
  description: 'Plows through terrain. Carves a trench along its flight path, explodes on tank hit or timeout.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 12,
  projectileSpeed: 0.45,
  color: 0x44ff00,
  gravity: 0.14,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 30,
  blastRadius: 130,
  knockbackMult: 0.075,
  knockbackRadius: 1.3,
  shakeIntensity: 2.0,
  particleScale: 2.5,
  craterShape: HUGE_CRATER,
  tracerIgnoreTerrain: true,
  onTick(proj, _state, fx) {
    // Carve through terrain each frame — the drill
    proj.ignoreTerrain = true;

    const carveRadius = 55;
    const bounds = clearCircle(proj.x, proj.y, carveRadius, true);
    if (bounds) {
      applyScorch(proj.x, proj.y, carveRadius);
      fx.push({ type: 'terrain_update', bounds });
      // Earth chunks displaced by the drill
      fx.push({ type: 'particles', method: 'spawnTerrainDebris', args: [proj.x, proj.y, carveRadius, 0.15] });
    }

    // Green energy trail — visible during arc and drilling
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 6,
        proj.y + (Math.random() - 0.5) * 6,
        0x44ff88,
        0.3, 0.8, 0.04, 0.08, 0.12, 0.25, 0, 0.92, true, true, 0.5,
      ] });
    }

    // Rumble while drilling
    const bs = proj.behaviorState;
    bs.flightFrames = (bs.flightFrames || 0) + 1;
    if (bs.flightFrames % 6 === 0) {
      fx.push({ type: 'screen_shake', intensity: 2.5, decay: 0.85 });
    }

    // Timeout: 4 seconds
    if (bs.flightFrames >= 240) {
      return 'detonate';
    }

    return null;
  },
});

// ─── Mason Weapons ───────────────────────────────────────────────────────────

// Shot 1: Dirt Ball — small terrain mound, utility over combat
const MASON_SHOT1 = registerWeapon({
  id: 'mason_shot1',
  name: 'Dirt Ball',
  description: 'Creates a small terrain mound. Plug gaps, build ramps, create surfaces.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 7,
  projectileSpeed: 0.55,
  color: 0x886644,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 5,            // S1 nerf: was 8
  blastRadius: 180,
  knockbackMult: 0.02,
  knockbackRadius: 0.5,
  shakeIntensity: 3.0,
  particleScale: 1.0,
  craterShape: DEFAULT_CRATER,
  fillsTerrain: true,
  // Brown dust cloud — earthy puffs, slow drift, large scale
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 4 === 0) {
      const color = Math.random() < 0.5 ? 0x8B6914 : 0x6B5010;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 8,
        proj.y + (Math.random() - 0.5) * 8,
        color,
        0.1, 0.4, 0.1, 0.2, 0.3, 0.6, 0.01, 0.97, false, false, 0.35,
      ] });
    }
    return null;
  },
});

// Shot 3: Citadel — fortress: wide platform + two side pillars
const MASON_SHOT3 = registerWeapon({
  id: 'mason_shot3',
  name: 'Citadel',
  description: 'Builds a fortress. Wide platform with wall pillars on each side.',
  tier: 'RARE',
  rarity: 'rare',
  projectileRadius: 10,
  projectileSpeed: 0.45,
  color: 0xcc9944,
  gravity: 0.18,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 20,
  blastRadius: 80,
  knockbackMult: 0.05,
  knockbackRadius: 1.2,
  shakeIntensity: 2.5,
  particleScale: 1.2,
  craterShape: DEFAULT_CRATER,
  fillsTerrain: true,
  // Amber/gold construction puffs — material being carried to the build site
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 3 === 0) {
      const color = Math.random() < 0.6 ? 0xddaa44 : 0xbb8833;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + (Math.random() - 0.5) * 10,
        proj.y + (Math.random() - 0.5) * 10,
        color,
        0.15, 0.5, 0.06, 0.12, 0.2, 0.4, 0.01, 0.96, false, false, 0.4,
      ] });
    }
    return null;
  },
  onImpact(_proj, x, y, _hitType, _hitIndex, _state, fx) {
    // Build two vertical pillars flanking the impact point
    const leftBounds = fillVerticalWall(x - 100, y, 420, 30);
    const rightBounds = fillVerticalWall(x + 100, y, 420, 30);
    if (leftBounds) {
      fx.push({ type: 'terrain_update', bounds: leftBounds });
    }
    if (rightBounds) {
      fx.push({ type: 'terrain_update', bounds: rightBounds });
    }
    fx.push({ type: 'screen_shake', intensity: 12, decay: 0.88 });
    fx.push({ type: 'announce_world', text: 'FORTRESS!', x, y: y - 100, color: 0xcc9944, fontSize: 36, hold: 0.8 });
    return [];
  },
});

// ─── Mode Switch Dummy Weapon ────────────────────────────────────────────────

// Dummy weapon for mode switch slot — never fired
const SWITCH_WEAPON = registerWeapon({
  id: 'terraformer_switch',
  name: 'Switch Mode',
  description: 'Switch between Sapper and Mason mode. Free action.',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 0,
  projectileSpeed: 0,
  color: 0xffaa00,
  gravity: 0,
  bounces: 0,
  bounceEnergyRetention: 0,
  damage: 0,
  blastRadius: 0,
  knockbackMult: 0,
  knockbackRadius: 0,
  shakeIntensity: 0,
  particleScale: 0,
  craterShape: DEFAULT_CRATER,
});

// ─── Terraformer Tank Registration ───────────────────────────────────────────

// Sapper mode: Digger (free) / Trencher (80M) / Switch (0M)
const TERRAFORMER_SAPPER_SHOTS: [
  { tier: 1; weapon: typeof SAPPER_SHOT1; momentumCost: number; hitlagBonus: number; label: string; tierColor: number },
  { tier: 2; weapon: typeof SAPPER_SHOT2; momentumCost: number; hitlagBonus: number; label: string; tierColor: number },
  { tier: 3; weapon: typeof SWITCH_WEAPON; momentumCost: number; hitlagBonus: number; label: string; tierColor: number },
] = [
  { tier: 1, weapon: SAPPER_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Digger', tierColor: 0x88ff44 },
  { tier: 2, weapon: SAPPER_SHOT2, momentumCost: 0, hitlagBonus: 25, label: 'Trencher', tierColor: 0x44ff00 },
  { tier: 3, weapon: SWITCH_WEAPON, momentumCost: 0, hitlagBonus: 0, label: 'SWITCH', tierColor: 0xffaa00 },
];

// Mason mode: Dirt Ball (free) / Citadel (100M) / Switch (0M)
setTerraformerMasonShots([
  { tier: 1, weapon: MASON_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Dirt Ball', tierColor: 0x886644 },
  { tier: 2, weapon: MASON_SHOT3, momentumCost: 0, hitlagBonus: 15, label: 'Citadel', tierColor: 0xcc9944 },
  { tier: 3, weapon: SWITCH_WEAPON, momentumCost: 0, hitlagBonus: 0, label: 'SWITCH', tierColor: 0xffaa00 },
]);

registerTank({
  id: 'terraformer',
  name: 'Terraformer',
  description: 'Reshape or rebuild. Switch modes to destroy or create terrain.',
  chargeSize: 95,
  visual: { bodyColor: 0x22CC44, glowColor: 0x33FF66, bodyRadius: 38 },
  ecbRadius: 32,
  shots: TERRAFORMER_SAPPER_SHOTS,
  bonus: true,
  onlineDisabled: true,
});
